import { FC, useState, useContext } from 'react';
import { Modal } from '@appkit4/react-components/modal';
import { Button } from '@appkit4/react-components/button';
import { SessionContext } from '@contexts/SessionProvider';
import { UserContext } from '@contexts/UserProvider';
import { pushToGithub } from '@config/getGithubApis';
import styles from './GithubLeaseFailedModal.module.scss';

interface GithubLeaseFailedModalProps {
    visible: boolean;
    onCancel: () => void;
}

const GithubLeaseFailedModal: FC<GithubLeaseFailedModalProps> = ({ visible, onCancel }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const { sessionId, setGithubLastPushedAt, setGithubLastPushedSha, setGithubLastPushStatus } =
        useContext(SessionContext);
    const { setGithubLastAuthFailureAt } = useContext(UserContext);

    const handleForcePush = async () => {
        setIsLoading(true);
        setErrorMessage('');

        try {
            const response = await pushToGithub(sessionId, true);
            if (!response || response.code !== 1) {
                const errCode = response?.resultObject?.errorCode || '';
                if (errCode === 'AUTH_FAILED') {
                    setGithubLastAuthFailureAt(new Date().toISOString());
                    onCancel();
                    return;
                }
                setErrorMessage(response?.description || 'Force push failed.');
                return;
            }

            const result = response.resultObject;
            setGithubLastPushedAt(result.pushedAt ?? new Date().toISOString());
            setGithubLastPushedSha(result.headSha ?? null);
            setGithubLastPushStatus('ok');
            onCancel();
        } catch (error: any) {
            const errCode = error?.response?.data?.errorCode || error?.response?.data?.resultObject?.errorCode || '';
            if (errCode === 'AUTH_FAILED') {
                setGithubLastAuthFailureAt(new Date().toISOString());
                onCancel();
            } else {
                const msg = error?.response?.data?.description || 'Force push failed.';
                setErrorMessage(msg);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        if (!isLoading) {
            setErrorMessage('');
            onCancel();
        }
    };

    return (
        <Modal
            visible={visible}
            title="Push conflict"
            ariaLabel="Push conflict"
            onCancel={handleCancel}
            modalStyle={{ width: '480px' }}
            footer={
                <>
                    <Button kind="secondary" onClick={handleCancel} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button kind="primary" onClick={handleForcePush} disabled={isLoading}>
                        {isLoading ? 'Pushing...' : 'Force push'}
                    </Button>
                </>
            }>
            <div className={styles['lease-failed-modal-body']}>
                <div className={styles['warning-icon']}>
                    <span className="Appkit4-icon icon-alert-fill" aria-hidden="true" />
                    Remote branch has diverged
                </div>
                <p className={styles['description']}>
                    The remote <code>design</code> branch was updated since your last push. Force-push to overwrite it?
                    This will discard any changes made directly to the remote branch.
                </p>
                {errorMessage && (
                    <p className={styles['error-message']}>
                        <span className="Appkit4-icon icon-alert-outline" aria-hidden="true" />
                        {errorMessage}
                    </p>
                )}
            </div>
        </Modal>
    );
};

export default GithubLeaseFailedModal;
